import { CommonModule } from '@angular/common';
import { Component, computed, input, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AwBadgeComponent,
  AwBreadCrumbComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { ParentIntegrationService } from '../../../../../core/services/parent-integration.service';
import { IntegrationService } from '../../../../../core/services/integration.service';
import { ParentIntegration } from '../../../../../api/models';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { ThemeService } from '../../../../../core/services/theme.service';
import { UserService, Organization } from '../../../../../core/services/user.service';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { Update_3$Params } from '../../../../../api/fn/parent-integrations/update-3';

@Component({
  selector: 'app-parent-integration-details',
  standalone: true,
  imports: [
    CommonModule,
    AwBreadCrumbComponent,
    AwButtonDirective,
    AwDividerComponent,
    AwSpinnerComponent,
    AwToastComponent,
    AwSearchComponent,
    AwTableComponent,
    AwDialogComponent,
    MatIcon,
    FormsModule,
  ],
  templateUrl: './parent-integration-details.component.html',
  styleUrls: [
    './parent-integration-details.component.scss',
    '../../../../../../styles/management.scss',
  ],
})
export class ParentIntegrationDetailsComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private parentIntegrationService: ParentIntegrationService,
    private integrationService: IntegrationService,
    private themeService: ThemeService,
    private userService: UserService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent) awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  parentIntegrationInfo!: ParentIntegration | undefined;
  parentIntegrationInfoDividers = signal<any[]>([]);
  parentIntegrationDateDividers = signal<any[]>([]);

  userPermissions: Record<string, boolean> = {};
  organization: Organization | null = null;
  container_theme = computed(() => this.themeService.themeSignal());

  integrationsTableData = signal<any[]>([]);
  integrationsTableDataCopy: any[] = [];
  integrationSearchValue: string = '';

  openDisassociateIntegrationDialogSingle: boolean = false;
  openChangeIntegrationStatus: boolean = false;
  openAssociateIntegrationDialog: boolean = false;

  checkedRows: TableRow[] = [];
  singleIntegrationToDisassociate: TableRow | null = null;

  disableIntegrationChangeStatus: boolean = true;
  disableIntegrationAssociate: boolean = true;

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Parent Integration Management',
      link: '/parent-integration-management/overview',
      newTab: false,
    },
  ]);

  dialogOptionsDisassociateSingle: DialogOptions = {
    variant: 'alert',
    title: 'Change Integration Partner Association',
    description: 'By selecting Continue, you will disassociate the selected Integration Partner from this Parent Integration. Choose Cancel to go back.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  dialogOptionsChangeStatus: DialogOptions = {
    variant: 'standard',
    title: 'Change Integration Partner Status',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  };

  dialogOptionsAssociate: DialogOptions = {
    variant: 'alert',
    title: 'Associate Integration Partners',
    description: 'By selecting Continue, you will associate the selected Integration Partner(s) with this Parent Integration. Choose Cancel to go back.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  changeStatusFormFields = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  integrationsColumnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'integrationInfo',
      label: 'Integration Partner Name/ID',
      combineFields: [
        'integrationInfo.integrationName',
        'integrationInfo.detailsPageRoute',
        'integrationInfo.integrationId',
      ],
      combineTemplate: (values: any[]) => ({
        template: this.CustomTemplate(values),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Associated',
      label: 'Associated',
      combineFields: ['associationBadge.color', 'associationBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
  ];

  CustomTemplate = (values: any) => `
  <div class="custom-template">
    <a href="${values[1]}">${values[0]}</a>
  <p>${values[2]}</p>
  </div>`;

  headerButtonActions = signal<any[]>([
    {
      iconButtonCallback: {
        icon: 'edit',
        action: () => {
          this.router.navigate(['edit'], { relativeTo: this.route });
        },
      },
    },
  ]);

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  ngOnInit(): void {
    this.userPermissions = this.userService.getUserPermissions();
    this.organization = this.userService.getOrganization();

    if (this.organization === 'integration-employee') {
      const associatedColumnIndex = this.integrationsColumnsDefinition.findIndex(
        col => col.key === 'Associated'
      );
      if (associatedColumnIndex !== -1) {
        this.integrationsColumnsDefinition.splice(associatedColumnIndex, 1);
      }
    }

    if (this.organization === 'assetworks-employee' && (this.userPermissions['canDelete'] || this.userPermissions['canEdit'])) {
      this.integrationsColumnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.organization === 'assetworks-employee' && this.userPermissions['canDelete']) {
      this.integrationsColumnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'deleteButton',
        label: 'Disassociate',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',
        action: (data: TableRow) => {
          if (data['associationBadge']?.isAssociated) {
            this.openDisassociateIntegrationDialogSingle = true;
            this.singleIntegrationToDisassociate = data;
          }
        },
      });
    }

    this.buildAllData();
  }

  buildAllData(): void {
    this.isLoading = true;

    this.route.paramMap
      .pipe(
        map((params) => {
          const parentIntegrationId = params.get('parentIntegrationId')!;
          this.breadcrumbs.set([
            ...this.breadcrumbs(),
            {
              label: 'Parent Integration Details',
              link: `/parent-integration-management/parent-integration-details/${parentIntegrationId}`,
              newTab: false,
            },
          ]);
          return parentIntegrationId;
        }),
        take(1),
        switchMap((parentIntegrationId) =>
          this.parentIntegrationService.getParentIntegrationById({
            organizationId: parentIntegrationId,
          })
        ),
        switchMap((parentIntegration) => {
          if (!parentIntegration) {
            return of([]);
          }

          this.parentIntegrationInfo = parentIntegration;

          this.parentIntegrationInfoDividers.set([
            {
              title: 'Parent Integration Name',
              info: parentIntegration.name ?? 'Unable to Retrieve',
            },
            {
              title: 'Organization ID',
              info: parentIntegration.organizationId ?? 'Unable to Retrieve',
            },
            {
              title: 'Status',
              info: parentIntegration.enabled ? 'Active' : 'Disabled',
            },
            {
              title: 'Employee Authentication',
              info: 'OpenID Connect',
            },
          ]);

          this.parentIntegrationDateDividers.set([
            {
              title: 'Created Date',
              info: parentIntegration.createdOn
                ? new Date(parentIntegration.createdOn).toLocaleDateString()
                : 'N/A',
            },
            {
              title: 'Last Modified Date',
              info: parentIntegration.modifiedOn
                ? new Date(parentIntegration.modifiedOn).toLocaleDateString()
                : 'N/A',
            },
          ]);

          // For integration employees, fetch only associated integrations
          // For AssetWorks employees, fetch all integrations
          if (this.organization === 'integration-employee') {
            const integrationIds = parentIntegration.integrationIds || [];
            if (integrationIds.length === 0) {
              return of([]);
            }
            return forkJoin(
              integrationIds.map(id =>
                this.integrationService.getIntegrationById({ organizationId: id })
                  .pipe(catchError(() => of(null)))
              )
            ).pipe(
              map(integrations => integrations.filter((i): i is any => i !== null)),
              catchError(() => {
                this.buildToastMessage('Failed to load integrations', 'error');
                return of([]);
              })
            );
          } else {
            return this.integrationService.getAlIntegrations().pipe(
              catchError(() => {
                this.buildToastMessage('Failed to load integrations', 'error');
                return of([]);
              }),
              map((integrations) => integrations || [])
            );
          }
        }),
        catchError(() => {
          this.buildToastMessage('Failed to load parent integration data', 'error');
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((integrations) => {
        const integrationsData = integrations.map((integration) => {
          const isAssociated =
            this.parentIntegrationInfo?.integrationIds?.includes(
              integration?.organizationId ?? ''
            ) ?? false;

          return {
            integrationInfo: {
              integrationName: integration?.organizationName ?? '',
              integrationId: integration?.organizationId ?? '',
              detailsPageRoute: `/integration-management/integration-details/${integration?.organizationId}`,
            },
            associationBadge: {
              isAssociated: isAssociated,
              color: isAssociated ? 'success-green-700' : 'error-red',
              label: isAssociated ? 'Yes' : 'No',
            },
            badgeNested: {
              enabled: integration?.enabled ?? false,
              color: integration?.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: integration?.enabled ? 'Active' : 'Disabled',
            },
            authType: integration?.authenticationType,
            customerIds: integration?.customerIds || [],
          };
        });

        this.integrationsTableData.set(integrationsData);
        this.integrationsTableDataCopy = integrationsData;
      });
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }): void {
    this.checkedRows = checkedRows.rows;
    const associatedRows = this.checkedRows.filter(
      (row) => row['associationBadge']?.isAssociated
    );
    const nonAssociatedRows = this.checkedRows.filter(
      (row) => !row['associationBadge']?.isAssociated
    );

    this.disableIntegrationChangeStatus = associatedRows.length === 0;
    this.disableIntegrationAssociate = nonAssociatedRows.length === 0;
  }

  onSearchTable(searchInput: string): void {
    this.awTableComponent.applyFilter(searchInput);
  }

  dialogFormCloseHandler(key: string): void {
    if (key === 'disassociate') {
      this.openDisassociateIntegrationDialogSingle = false;
    } else if (key === 'change-status') {
      this.openChangeIntegrationStatus = false;
    } else if (key === 'associate') {
      this.openAssociateIntegrationDialog = false;
    }
  }

  disassociateIntegrationFormSubmit(): void {
    if (!this.singleIntegrationToDisassociate) {
      this.buildToastMessage('No integration selected', 'error');
      return;
    }

    this.isLoading = true;

    const removedIntegrationId =
      this.singleIntegrationToDisassociate['integrationInfo'].integrationId;
    const newIntegrationIds = (
      this.parentIntegrationInfo?.integrationIds ?? []
    ).filter((id) => id !== removedIntegrationId);

    const params: Update_3$Params = {
      organizationId: this.parentIntegrationInfo?.organizationId || '',
      body: {
        name: this.parentIntegrationInfo?.name ?? '',
        enabled: this.parentIntegrationInfo?.enabled || false,
        integrationIds: newIntegrationIds,
      },
    };

    this.parentIntegrationService.updateParentIntegration(params).subscribe((success) => {
      if (success) {
        this.buildToastMessage('Successfully disassociated integration partner', 'success');

        this.parentIntegrationInfo!.integrationIds = newIntegrationIds;

        const integrationToUpdate = this.integrationsTableDataCopy.find(
          (integration) =>
            integration['integrationInfo'].integrationId === removedIntegrationId
        );
        if (integrationToUpdate) {
          integrationToUpdate['associationBadge'].isAssociated = false;
          integrationToUpdate['associationBadge'].color = 'error-red';
          integrationToUpdate['associationBadge'].label = 'No';
        }
        this.integrationsTableData.set([...this.integrationsTableDataCopy]);
      } else {
        this.buildToastMessage('Failed to disassociate integration partner', 'error');
      }
      this.openDisassociateIntegrationDialogSingle = false;
      this.isLoading = false;
    });
  }

  associateIntegrationsFormSubmit(): void {
    const nonAssociatedIntegrations = this.checkedRows.filter(
      (r) => !r['associationBadge']?.isAssociated
    );

    if (!nonAssociatedIntegrations.length) {
      this.buildToastMessage('No non-associated integrations selected', 'error');
      return;
    }

    this.isLoading = true;

    const integrationIdsToAdd = nonAssociatedIntegrations.map(
      (i) => i['integrationInfo'].integrationId
    );
    const newIntegrationIds = [
      ...(this.parentIntegrationInfo?.integrationIds ?? []),
      ...integrationIdsToAdd,
    ];

    const params: Update_3$Params = {
      organizationId: this.parentIntegrationInfo?.organizationId || '',
      body: {
        name: this.parentIntegrationInfo?.name ?? '',
        enabled: this.parentIntegrationInfo?.enabled || false,
        integrationIds: newIntegrationIds,
      },
    };

    this.parentIntegrationService
      .updateParentIntegration(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openAssociateIntegrationDialog = false;
        })
      )
      .subscribe((success) => {
        if (success) {
          this.parentIntegrationInfo!.integrationIds = newIntegrationIds;

          nonAssociatedIntegrations.forEach((integration) => {
            integration['associationBadge'].isAssociated = true;
            integration['associationBadge'].color = 'success-green-700';
            integration['associationBadge'].label = 'Yes';
          });

          this.buildToastMessage(
            `Successfully associated ${integrationIdsToAdd.length} integration partner(s)`,
            'success'
          );
        } else {
          this.buildToastMessage('Failed to associate integration partners', 'error');
        }
      });
  }

  changeIntegrationStatusFormSubmit(formValues: any): void {
    if (!formValues.valid) {
      this.buildToastMessage('Please select a status', 'alert');
      return;
    }
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }

    const status: boolean = formValues.formData.changeStatus.value;

    const associatedIntegrations = this.checkedRows.filter(
      (r) => r['associationBadge']?.isAssociated
    );
    const integrationsToChange = associatedIntegrations.filter(
      (r) => r['badgeNested'].enabled !== status
    );

    if (!integrationsToChange.length) {
      this.buildToastMessage(
        `No ${!status ? 'active' : 'disabled'} rows selected`,
        'alert'
      );
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = integrationsToChange.map(
      (integration) => {
        const integrationInfo = integration['integrationInfo'];

        return this.integrationService
          .updateIntegration({
            organizationAuthType: integration['authType'],
            organizationName: integrationInfo.integrationName,
            organizationStatus: status,
            organizationPartnerIds: integration['customerIds'],
            organizationId: integrationInfo.integrationId,
          })
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeIntegrationStatus = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates = results.filter((updated) => updated).length;

        integrationsToChange.forEach((integration, index) => {
          if (results[index]) {
            const badge = integration['badgeNested'];
            badge.enabled = status;
            badge.color = badge.enabled
              ? 'success-green-700'
              : 'aw-dark-gray-1000';
            badge.label = badge.enabled ? 'Active' : 'Disabled';
          }
        });

        if (numStatusUpdates === integrationsToChange.length) {
          this.buildToastMessage(
            `Successfully ${
              status ? 'enabled' : 'disabled'
            } all selected integration partners`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `ALERT: ${numStatusUpdates} of ${
              integrationsToChange.length
            } integration partners ${status ? 'enabled' : 'disabled'}`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            `Failed to ${
              status ? 'enable' : 'disable'
            } any selected integration partners`,
            'error'
          );
        }
      });
  }
}
